import React, { useState } from 'react';
import { Filter, SortAsc, SortDesc, FileText, Target } from 'lucide-react';
import CaseCard from './CaseCard';
import AnalyticsPanel from './AnalyticsPanel';
import PropTypes from 'prop-types';
import { DocketPropType } from '../../../types/propTypes';
import { formatDateForDisplay } from '../../../utils/dateUtils';

const EnhancedResultsSection = ({
  results = [],
  onCaseSelect,
  onFindSimilar,
  pagination = {
    currentPage: 1,
    pageSize: 20,
    totalResults: 0
  },
  onPageChange,
  isLoading = false,
  searchQuery = '',
  filters = {},
  sortBy = 'date',
  sortOrder = 'desc',
  onSortChange,
  sourceCase = null,
  executionTime = null
}) => {
  const [selectedCases, setSelectedCases] = useState([]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!Array.isArray(results) || results.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="max-w-md mx-auto space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <Filter className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">
            No results found
          </h3>
          <p className="text-gray-500">
            {searchQuery || Object.keys(filters).length > 0
              ? 'Try adjusting your search criteria or filters'
              : 'Start by searching for a case number, judge, or charge'}
          </p>
          {searchQuery && (
            <div className="text-sm text-gray-400">
              Search: &quot;{searchQuery}&quot;
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleToggleSelection = (caseData) => {
    setSelectedCases((prev) => {
      const isAlreadySelected = prev.some(
        (c) => c.caseNumber === caseData.caseNumber
      );
      if (isAlreadySelected) {
        return prev.filter((c) => c.caseNumber !== caseData.caseNumber);
      } else {
        return [...prev, { ...caseData }];
      }
    });
  };

  const handleClearAll = () => {
    setSelectedCases([]);
  };

  const handlePrintReport = async (cases) => {
    // Generate print content
    const printContent = generatePrintContent(cases);

    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      alert('Please allow popups to print the report.');
      return;
    }

    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Case Report - ${new Date().toLocaleDateString()}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 10pt;
              line-height: 1.4;
              color: #000;
              background-color: #fff;
              margin: 0;
              padding: 0.5cm;
            }
            
            .printable-report {
              width: 100%;
              margin: 0;
              padding: 0;
            }
            
            .printable-report h1 {
              font-size: 16pt;
              margin-bottom: 8pt;
            }
            
            .printable-report h2 {
              font-size: 14pt;
              margin-bottom: 6pt;
              page-break-after: avoid;
            }
            
            .printable-report h3 {
              font-size: 12pt;
              margin-bottom: 4pt;
              page-break-after: avoid;
            }
            
            .case-item {
              margin-bottom: 12pt;
              padding-bottom: 8pt;
              border-bottom: 1px solid #ddd;
              page-break-inside: avoid;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 6pt;
            }
            
            th, td {
              padding: 2pt 4pt;
              text-align: left;
              border: 1px solid #ddd;
              font-size: 9pt;
            }
            
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            
            ul {
              margin: 2pt 0;
              padding-left: 12pt;
            }
            
            li {
              margin-bottom: 1pt;
              font-size: 9pt;
            }
            
            .grid {
              display: table;
              width: 100%;
            }
            
            .grid-cols-2 {
              display: table-row;
            }
            
            .grid > div {
              display: table-cell;
              width: 50%;
              vertical-align: top;
              padding-right: 8pt;
            }
            
            .grid > div:last-child {
              padding-right: 0;
            }
            
            .text-xs { font-size: 9pt; }
            .text-sm { font-size: 10pt; }
            .text-lg { font-size: 12pt; }
            .text-xl { font-size: 14pt; }
            
            .font-bold { font-weight: bold; }
            .font-medium { font-weight: 500; }
            
            .mb-1 { margin-bottom: 2pt; }
            .mb-2 { margin-bottom: 4pt; }
            .mb-3 { margin-bottom: 6pt; }
            .mb-4 { margin-bottom: 8pt; }
            .mb-5 { margin-bottom: 10pt; }
            
            .mt-1 { margin-top: 2pt; }
            .mt-2 { margin-top: 4pt; }
            .mt-8 { margin-top: 16pt; }
            
            .pb-3 { padding-bottom: 6pt; }
            .pb-4 { padding-bottom: 8pt; }
            
            .border-b { border-bottom: 1px solid #ddd; }
            
            .text-center { text-align: center; }
            .text-gray-500 { color: #666; }
            .text-gray-600 { color: #666; }
            .text-blue-900 { color: #1e3a8a; }
            .text-red-700 { color: #b91c1c; }
            .text-orange-700 { color: #c2410c; }
            .text-green-700 { color: #15803d; }
            
            .px-2 { padding-left: 4pt; padding-right: 4pt; }
            .py-1 { padding-top: 2pt; padding-bottom: 2pt; }
            .py-0.5 { padding-top: 1pt; padding-bottom: 1pt; }
            .pr-2 { padding-right: 4pt; }
            .ml-1 { margin-left: 2pt; }
            
            .rounded-full { border-radius: 50%; }
            .border { border: 1px solid #ddd; }
            
            .w-1/3 { width: 33.333333%; }
            .w-full { width: 100%; }
            
            .flex { display: flex; }
            .items-center { align-items: center; }
            .justify-between { justify-content: space-between; }
            .justify-center { justify-content: center; }
            
            .gap-3 > * + * { margin-left: 6pt; }
            
            .pl-5 { padding-left: 10pt; }
            
            .list-disc { list-style-type: disc; }
            
            .bg-gray-50 { background-color: #f9fafb; }
            
            .border-t { border-top: 1px solid #ddd; }
            
            .last:border-b-0:last-child { border-bottom: none; }
            
            /* Analytics specific styles */
            .space-y-3 > * + * { margin-top: 6pt; }
            .space-y-2 > * + * { margin-top: 4pt; }
            .space-y-1 > * + * { margin-top: 2pt; }
            .rounded { border-radius: 2pt; }
            .bg-gray-50 { background-color: #f9fafb; }
            .border { border: 1px solid #ddd; }
            .grid { display: table; width: 100%; }
            .grid-cols-2 { display: table-row; }
            .grid-cols-2 > * { display: table-cell; width: 50%; vertical-align: top; padding-right: 8pt; }
            .grid-cols-2 > *:last-child { padding-right: 0; }
            
            @page {
              margin: 1cm;
              size: A4;
            }
            
            @media print {
              body { margin: 0; padding: 0; }
              .printable-report { margin: 0; padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="printable-report">
            ${printContent}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printDocument);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();

      setTimeout(() => {
        printWindow.close();
      }, 1000);
    };
  };

  const handleExportReport = async (cases) => {
    console.log('Exporting report with cases:', cases);
    // TODO: Implement export functionality
  };

  const generatePrintContent = (cases) => {
    if (!cases || cases.length === 0) {
      return '<div class="text-center text-gray-500">No cases selected for printing.</div>';
    }

    // Helper functions
    const formatDate = (dateString) => {
      return formatDateForDisplay(dateString);
    };

    const getTotalSentence = (counts) => {
      if (!counts || !counts.length) return 0;
      const total = counts.reduce((total, count) => {
        if (!count.sentence) return total;
        const { value, unit } = count.sentence;
        if (unit === 'months') return total + value;
        if (unit === 'years') return total + value * 12;
        if (unit === 'days') return total + value / 30.44;
        return total;
      }, 0);
      return total;
    };

    const formatSentence = (months) => {
      if (!months || months === 0 || isNaN(months)) return 'No sentence';
      if (months < 12) return `${months.toFixed(1)} months`;
      return `${(months / 12).toFixed(1)} years`;
    };

    const renderNames = (persons) => {
      if (!persons || !persons.length) return 'N/A';
      const realPersons = persons.filter(
        (person) => person.firstName && person.firstName !== 'Department'
      );
      if (realPersons.length === 0) return 'N/A';
      return realPersons
        .map((person) => {
          const name =
            person.firstName && person.lastName
              ? `${person.firstName} ${person.lastName}`
              : person.firstName || person.lastName || 'Unknown';
          return name;
        })
        .join(', ');
    };

    const generateCaseContent = (caseData, index) => {
      return `
        <div class="case-item mb-5 pb-4 border-b last:border-b-0 page-break-inside-avoid">
          <!-- Case Header -->
          <div class="flex justify-between items-center mb-2">
            <h2 class="text-lg font-bold">
              Case #${caseData.caseNumber}
            </h2>
            <div class="text-sm">
              <span class="px-2 py-1 text-xs rounded-full border">
                ${caseData.caseStatus || 'Unknown'}
              </span>
            </div>
          </div>

          <!-- Basic Info + Court Personnel in a 2-column grid -->
          <div class="grid grid-cols-2 gap-3 text-sm mb-3">
            <div>
              <table class="w-full text-xs">
                <tbody>
                  <tr>
                    <td class="py-0.5 pr-2 font-medium w-1/3">Court:</td>
                    <td>${caseData.courthouse || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="py-0.5 pr-2 font-medium w-1/3">Filed Date:</td>
                    <td>${formatDate(caseData.openDate)}</td>
                  </tr>
                  <tr>
                    <td class="py-0.5 pr-2 font-medium w-1/3">Last Action:</td>
                    <td>${formatDate(caseData.lastActionDate)}</td>
                  </tr>
                  <tr>
                    <td class="py-0.5 pr-2 font-medium w-1/3">Total Sentence:</td>
                    <td>${formatSentence(getTotalSentence(caseData.counts))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <table class="w-full text-xs">
                <tbody>
                  <tr>
                    <td class="py-0.5 pr-2 font-medium w-1/3">Judge(s):</td>
                    <td>${renderNames(caseData.judges)}</td>
                  </tr>
                  <tr>
                    <td class="py-0.5 pr-2 font-medium w-1/3">Prosecutor(s):</td>
                    <td>${renderNames(caseData.prosecutingAttorneys)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Charges -->
          <div class="mb-3">
            <h3 class="font-medium mb-1">Charges & Counts</h3>
            <ul class="pl-5 list-disc text-xs">
              ${caseData.counts
                .map(
                  (count, idx) => `
                <li>
                  <strong>${count.chargeCode}</strong>: ${count.chargeDescription}
                  ${count.sentence ? `<span class="ml-1 font-medium text-blue-900">- Sentence: ${count.sentence.value} ${count.sentence.unit}</span>` : ''}
                  ${count.fine && count.fine.value > 0 ? `<span class="ml-1 text-green-700">- Fine: $${count.fine.value.toLocaleString()}</span>` : ''}
                </li>
              `
                )
                .join('')}
            </ul>
          </div>

          <!-- Parties -->
          <div class="mb-3">
            <h3 class="font-medium mb-1">Case Parties</h3>
            <div class="grid grid-cols-2 gap-3 text-xs">
              <div>
                <h4 class="font-medium mb-0.5">Defendants</h4>
                <ul class="pl-5 list-disc">
                  ${caseData.defendants
                    .map(
                      (defendant, idx) => `
                    <li>
                      ${defendant.firstName} ${defendant.lastName}
                      ${
                        defendant.age || defendant.gender || defendant.race
                          ? `
                        <div class="text-gray-500 ml-1">
                          ${[defendant.gender, defendant.age !== null ? `${defendant.age} years` : null, defendant.race].filter(Boolean).join(' • ')}
                        </div>
                      `
                          : ''
                      }
                    </li>
                  `
                    )
                    .join('')}
                </ul>
              </div>
              <div>
                <h4 class="font-medium mb-0.5">Defense Attorneys</h4>
                <ul class="pl-5 list-disc">
                  ${caseData.defenseAttorneys
                    .map(
                      (attorney, idx) => `
                    <li>${attorney.firstName} ${attorney.lastName}</li>
                  `
                    )
                    .join('')}
                </ul>
              </div>
            </div>
          </div>
        </div>
      `;
    };

    // Generate analytics for the report
    const generateAnalyticsContent = (cases) => {
      if (!cases || cases.length === 0) return '';

      // Helper function to get primary charge
      const getPrimaryCharge = (counts) => {
        if (!counts || !counts.length) return 'Unknown Charge';
        let primaryCharge = counts[0];
        let maxSentence = getTotalSentence([counts[0]]);
        for (const count of counts) {
          const sentence = getTotalSentence([count]);
          if (sentence > maxSentence) {
            maxSentence = sentence;
            primaryCharge = count;
          }
        }
        return primaryCharge.chargeDescription || 'Unknown Charge';
      };

      // Helper function to get detailed outcome information for a specific charge
      const getChargeOutcomeDetails = (chargeData) => {
        if (!chargeData) return 'No outcome data';

        // Check for dismissal
        if (chargeData.isDismissed) return 'Dismissed';

        // Check sentencing types
        if (
          chargeData.sentencingTypes &&
          chargeData.sentencingTypes.length > 0
        ) {
          const types = chargeData.sentencingTypes;
          if (types.includes('Dismissed')) return 'Dismissed';
          if (types.includes('Diversion')) return 'Diversion';
          if (types.includes('Probation')) return 'Probation';
          if (types.includes('Confinement')) return 'Confinement';
          if (types.includes('Fine')) return 'Fine';
          return 'Resolved';
        }

        // Check for actual sentencing data
        if (chargeData.sentencing) {
          const { confinements, probations, fines } = chargeData.sentencing;
          if (confinements && confinements.length > 0) return 'Confinement';
          if (probations && probations.length > 0) return 'Probation';
          if (fines && fines.length > 0) return 'Fine';
        }

        // Check for sentence data
        if (chargeData.sentence) {
          return 'Sentenced';
        }

        return 'No outcome data';
      };

      // Helper function to get disposition summary (same as in AnalyticsPanel)
      const getDispositionSummary = (caseData) => {
        if (!caseData) return 'Unknown';

        // Use case-level dismissal status
        if (caseData.isDismissed) return 'Dismissed';

        // Use sentencing types to determine outcome
        if (caseData.sentencingTypes && caseData.sentencingTypes.length > 0) {
          // If it has sentencing types, it's likely resolved
          if (caseData.sentencingTypes.includes('Dismissed'))
            return 'Dismissed';
          if (caseData.sentencingTypes.includes('Diversion'))
            return 'Diversion';
          if (caseData.sentencingTypes.includes('Confinement'))
            return 'Confinement';
          if (caseData.sentencingTypes.includes('Probation'))
            return 'Probation';
          if (caseData.sentencingTypes.includes('Fine')) return 'Fine';
          return 'Resolved';
        }

        // Check if case has sentences (indicating it's resolved)
        if (caseData.counts && caseData.counts.length > 0) {
          const hasSentence = caseData.counts.some(
            (count) =>
              count.sentence ||
              (count.sentencing &&
                (count.sentencing.confinements?.length > 0 ||
                  count.sentencing.probations?.length > 0))
          );

          if (hasSentence) return 'Resolved';
        }

        // Check case status
        if (caseData.caseStatus) {
          const status = caseData.caseStatus.toLowerCase();
          if (status.includes('closed') || status.includes('disposed'))
            return 'Closed';
          if (status.includes('active') || status.includes('open'))
            return 'Active';
        }

        // Default based on whether case has a close date
        if (caseData.closeDate) return 'Closed';

        return 'Active';
      };

      // Calculate charge breakdown
      const chargeBreakdown = {};
      const judgeAnalytics = {};
      const outcomeAnalysis = {
        totalCases: cases.length,
        dispositions: {},
        caseStatuses: {},
        averageDuration: 0
      };

      cases.forEach((caseData) => {
        const primaryCharge = getPrimaryCharge(caseData.counts);
        const disposition = getDispositionSummary(caseData);
        const caseStatus = caseData.caseStatus || 'Unknown';

        // Get the sentence for the primary charge specifically
        const primaryChargeData = caseData.counts.find(
          (count) => count.chargeDescription === primaryCharge
        );
        const chargeSpecificSentence = primaryChargeData
          ? getTotalSentence([primaryChargeData])
          : 0;
        const chargeOutcome = primaryChargeData
          ? getChargeOutcomeDetails(primaryChargeData)
          : 'No outcome data';

        // Charge breakdown
        if (!chargeBreakdown[primaryCharge]) {
          chargeBreakdown[primaryCharge] = {
            count: 0,
            sentences: [],
            caseOutcomes: [], // Store case number with sentence and outcome
            averageSentence: 0
          };
        }
        chargeBreakdown[primaryCharge].count++;
        chargeBreakdown[primaryCharge].sentences.push(chargeSpecificSentence);
        chargeBreakdown[primaryCharge].caseOutcomes.push({
          caseNumber: caseData.caseNumber,
          sentence: chargeSpecificSentence,
          outcome: chargeOutcome
        });

        // Judge analytics
        if (caseData.judges && caseData.judges.length > 0) {
          caseData.judges.forEach((judge) => {
            if (judge.firstName && judge.firstName !== 'Department') {
              const judgeName = `${judge.firstName} ${judge.lastName}`;
              if (!judgeAnalytics[judgeName]) {
                judgeAnalytics[judgeName] = {
                  totalCases: 0,
                  averageSentence: 0,
                  sentences: [],
                  charges: {}
                };
              }
              judgeAnalytics[judgeName].totalCases++;
              judgeAnalytics[judgeName].sentences.push(chargeSpecificSentence);

              // Track charges for this judge
              if (!judgeAnalytics[judgeName].charges[primaryCharge]) {
                judgeAnalytics[judgeName].charges[primaryCharge] = {
                  count: 0,
                  sentences: [],
                  caseOutcomes: []
                };
              }
              judgeAnalytics[judgeName].charges[primaryCharge].count++;
              judgeAnalytics[judgeName].charges[primaryCharge].sentences.push(
                chargeSpecificSentence
              );
              judgeAnalytics[judgeName].charges[
                primaryCharge
              ].caseOutcomes.push({
                caseNumber: caseData.caseNumber,
                sentence: chargeSpecificSentence,
                outcome: chargeOutcome
              });
            }
          });
        }

        // Outcome analysis
        if (!outcomeAnalysis.dispositions[disposition]) {
          outcomeAnalysis.dispositions[disposition] = 0;
        }
        outcomeAnalysis.dispositions[disposition]++;

        if (!outcomeAnalysis.caseStatuses[caseStatus]) {
          outcomeAnalysis.caseStatuses[caseStatus] = 0;
        }
        outcomeAnalysis.caseStatuses[caseStatus]++;

        // Add to average duration calculation
        outcomeAnalysis.averageDuration += caseData.durationDays || 0;
      });

      // Calculate averages
      Object.keys(chargeBreakdown).forEach((charge) => {
        const data = chargeBreakdown[charge];
        data.averageSentence =
          data.sentences.reduce((sum, s) => sum + s, 0) / data.sentences.length;
      });

      Object.keys(judgeAnalytics).forEach((judge) => {
        const data = judgeAnalytics[judge];
        data.averageSentence =
          data.sentences.reduce((sum, s) => sum + s, 0) / data.sentences.length;

        // Calculate averages for each charge type
        Object.keys(data.charges).forEach((charge) => {
          const chargeData = data.charges[charge];
          chargeData.averageSentence =
            chargeData.sentences.reduce((sum, s) => sum + s, 0) /
            chargeData.sentences.length;
        });
      });

      // Calculate average duration
      outcomeAnalysis.averageDuration =
        outcomeAnalysis.averageDuration / cases.length;

      const formatMonths = (months) => {
        if (!months || months === 0 || isNaN(months)) return 'No sentence';
        if (months < 12) return `${months.toFixed(1)} months`;
        return `${(months / 12).toFixed(1)} years`;
      };

      return `
        <!-- Analytics Section -->
        <div class="mb-6 pb-4 border-b">
          <h2 class="text-lg font-bold mb-4">Case Analytics</h2>
          
          <!-- Charge-Specific Analysis -->
          <div class="mb-4">
            <h3 class="text-md font-semibold mb-3">Charge-Specific Analysis</h3>
            <div class="space-y-3">
              ${Object.entries(chargeBreakdown)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(
                  ([charge, data]) => `
                  <div class="bg-gray-50 p-3 rounded border">
                    <div class="flex justify-between items-start mb-2">
                      <h4 class="font-medium text-sm">${charge}</h4>
                      <span class="text-xs text-gray-600">${data.count} cases</span>
                    </div>
                    <div class="text-xs text-gray-700">
                      <div class="flex justify-between">
                        <span>Average Sentence:</span>
                        <span class="font-medium">${formatMonths(data.averageSentence)}</span>
                      </div>
                      <div class="mt-1">
                        <span class="text-gray-600">Individual Outcomes:</span>
                        <div class="mt-1 space-y-1">
                          ${data.caseOutcomes
                            .sort((a, b) => b.sentence - a.sentence)
                            .slice(0, 5)
                            .map(
                              (caseOutcome, idx) => `
                              <div class="flex justify-between text-xs">
                                <span>${caseOutcome.caseNumber}:</span>
                                <span class="font-medium">
                                  ${
                                    caseOutcome.sentence > 0
                                      ? formatMonths(caseOutcome.sentence)
                                      : caseOutcome.outcome
                                  }
                                </span>
                              </div>
                            `
                            )
                            .join('')}
                          ${
                            data.caseOutcomes.length > 5
                              ? `
                            <div class="text-xs text-gray-500 italic">
                              ... and ${data.caseOutcomes.length - 5} more
                            </div>
                          `
                              : ''
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                `
                )
                .join('')}
            </div>
          </div>

          <!-- Judge Analytics -->
          ${
            Object.keys(judgeAnalytics).length > 0
              ? `
            <div class="mb-4">
              <h3 class="text-md font-semibold mb-3">Judge-Specific Analytics</h3>
              <div class="space-y-4">
                ${Object.entries(judgeAnalytics)
                  .sort(([, a], [, b]) => b.totalCases - a.totalCases)
                  .map(
                    ([judge, data]) => `
                    <div class="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 class="font-semibold text-gray-900 mb-3 text-sm">${judge}</h5>
                      
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div>
                          <span class="text-gray-600">Total Cases:</span>
                          <span class="font-medium ml-2">${data.totalCases}</span>
                        </div>
                        <div>
                          <span class="text-gray-600">Avg Sentence:</span>
                          <span class="font-medium ml-2">${formatMonths(data.averageSentence)}</span>
                        </div>
                        <div>
                          <span class="text-gray-600">Charge Types:</span>
                          <span class="font-medium ml-2">${Object.keys(data.charges || {}).length}</span>
                        </div>
                      </div>

                      ${
                        Object.keys(data.charges || {}).length > 0
                          ? `
                        <div class="mt-3 pt-3 border-t border-gray-100">
                          <p class="text-xs font-medium text-gray-700 mb-2">By Charge Type:</p>
                          <div class="space-y-2">
                            ${Object.entries(data.charges)
                              .sort(([, a], [, b]) => b.count - a.count)
                              .slice(0, 3)
                              .map(
                                ([charge, chargeData]) => `
                                <div class="space-y-1">
                                  <div class="flex justify-between text-xs">
                                    <span class="text-gray-600 truncate flex-1 mr-2">
                                      ${charge.length > 30 ? charge.substring(0, 30) + '...' : charge}
                                    </span>
                                    <span class="font-medium">
                                      ${chargeData.count} cases, avg ${formatMonths(chargeData.averageSentence)}
                                    </span>
                                  </div>
                                  <div class="ml-2 space-y-1">
                                    ${chargeData.caseOutcomes
                                      .slice(0, 2)
                                      .map(
                                        (caseOutcome, idx) => `
                                        <div class="flex justify-between text-xs text-gray-500">
                                          <span>${caseOutcome.caseNumber}:</span>
                                          <span>
                                            ${
                                              caseOutcome.sentence > 0
                                                ? formatMonths(
                                                    caseOutcome.sentence
                                                  )
                                                : caseOutcome.outcome
                                            }
                                          </span>
                                        </div>
                                      `
                                      )
                                      .join('')}
                                    ${
                                      chargeData.caseOutcomes.length > 2
                                        ? `
                                      <div class="text-xs text-gray-400 italic">
                                        ... and ${chargeData.caseOutcomes.length - 2} more
                                      </div>
                                    `
                                        : ''
                                    }
                                  </div>
                                </div>
                              `
                              )
                              .join('')}
                          </div>
                        </div>
                      `
                          : ''
                      }
                    </div>
                  `
                  )
                  .join('')}
              </div>
            </div>
          `
              : ''
          }

          <!-- Case Outcome Analysis -->
          <div class="mb-4">
            <h3 class="text-md font-semibold mb-3">Case Outcome Analysis</h3>
            <div class="grid grid-cols-2 gap-4 text-sm">
              ${Object.entries(outcomeAnalysis.dispositions)
                .sort(([, a], [, b]) => b - a)
                .map(([outcome, count]) => {
                  const percentage = (count / outcomeAnalysis.totalCases) * 100;

                  // Add helpful descriptions for outcomes
                  const getOutcomeDescription = (outcome) => {
                    switch (outcome) {
                      case 'Dismissed':
                        return 'Case was dismissed';
                      case 'Diversion':
                        return 'Defendant entered diversion program';
                      case 'Confinement':
                        return 'Defendant received jail/prison time';
                      case 'Probation':
                        return 'Defendant received probation';
                      case 'Fine':
                        return 'Defendant received fine only';
                      case 'Resolved':
                        return 'Case resolved with sentencing';
                      case 'Closed':
                        return 'Case closed without sentencing';
                      case 'Active':
                        return 'Case still active/ongoing';
                      default:
                        return '';
                    }
                  };

                  return `
                    <div class="flex justify-between items-start">
                      <div>
                        <span class="text-gray-700 font-medium">${outcome}</span>
                        ${
                          getOutcomeDescription(outcome)
                            ? `
                          <div class="text-xs text-gray-500">${getOutcomeDescription(outcome)}</div>
                        `
                            : ''
                        }
                      </div>
                      <div class="flex items-center gap-2">
                        <span class="text-gray-600">${count}</span>
                        <span class="text-xs text-gray-500">(${percentage.toFixed(1)}%)</span>
                      </div>
                    </div>
                  `;
                })
                .join('')}
            </div>
            
            <!-- Average Duration -->
            <div class="mt-4 pt-4 border-t border-gray-200">
              <div class="flex items-center justify-between text-sm">
                <span class="text-gray-700">Average Case Duration:</span>
                <span class="font-medium text-gray-900">
                  ${
                    outcomeAnalysis.averageDuration > 0
                      ? `${Math.round(outcomeAnalysis.averageDuration)} days`
                      : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>

          <!-- Case Status Summary -->
          <div class="mb-4">
            <h3 class="text-md font-semibold mb-3">Case Status Summary</h3>
            <div class="grid grid-cols-2 gap-4 text-sm">
              ${Object.entries(outcomeAnalysis.caseStatuses)
                .sort(([, a], [, b]) => b - a)
                .map(([status, count]) => {
                  const percentage = (count / outcomeAnalysis.totalCases) * 100;
                  return `
                    <div class="flex justify-between items-center">
                      <span class="text-gray-700">${status}</span>
                      <span class="font-medium">${count} (${percentage.toFixed(1)}%)</span>
                    </div>
                  `;
                })
                .join('')}
            </div>
          </div>
        </div>
      `;
    };

    return `
      <!-- Report Header -->
      <div class="mb-4 pb-3 border-b">
        <div class="flex justify-between items-center">
          <h1 class="text-xl font-bold">Briefed Case Report</h1>
          <div class="text-sm text-gray-600">
            Generated: ${new Date().toLocaleDateString()}
          </div>
        </div>

        <div class="text-xs mt-1">
          Showing ${cases.length} selected case${cases.length !== 1 ? 's' : ''}
        </div>
      </div>

      <!-- Analytics Section -->
      ${generateAnalyticsContent(cases)}

      <!-- Cases -->
      ${cases.map((caseData, index) => generateCaseContent(caseData, index)).join('')}

      <div class="mt-8 text-center text-xs text-gray-500">
        End of Report | Briefed | Generated ${new Date().toLocaleDateString()}
      </div>
    `;
  };

  // In similarity mode, always show source case first
  const getFilteredResults = () => {
    // If in similarity mode and we have a source case, ensure it's first
    if (filters.similarTo && sourceCase) {
      // Remove source case from results if it exists
      const resultsWithoutSource = results.filter(
        (result) => result.caseNumber !== sourceCase.caseNumber
      );
      // Always put source case first
      return [sourceCase, ...resultsWithoutSource];
    }

    return results;
  };

  const filteredResults = getFilteredResults();
  const paginatedResults = filteredResults;

  const renderPageNumbers = () => {
    const totalResults = pagination.totalResults;
    const totalPages = Math.ceil(totalResults / pagination.pageSize);
    const page = pagination.currentPage;

    if (totalPages <= 1) return null;

    let pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return (
      <div className="flex items-center justify-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>

        {pages.map((pageNum, idx) =>
          pageNum === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-500">
              ...
            </span>
          ) : (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-2 text-sm border rounded-md ${
                pageNum === page
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              {pageNum}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        {/* Results Count and Selection Status - Top Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-gray-900">
                {pagination.totalResults.toLocaleString()}{' '}
                {pagination.totalResults === 1 ? 'case' : 'cases'} found
              </h2>
              {executionTime && (
                <span className="text-sm text-gray-500">
                  in {(executionTime + 1).toFixed(1)}s
                </span>
              )}
            </div>

            {selectedCases.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 border border-blue-200 rounded-lg text-sm font-medium">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {selectedCases.length} selected for report
                </span>
                <span className="sm:hidden">
                  {selectedCases.length} selected
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Controls Row - Bottom */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Sort Controls - Hidden in similarity mode */}
          {!filters.similarTo && (
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value, sortOrder)}
                className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-blue-500 min-w-0 sm:min-w-[180px]"
              >
                <option value="date">Sort by Filing Date</option>
                <option value="confinement">Sort by Confinement</option>
                <option value="fines">Sort by Fines</option>
              </select>

              <button
                onClick={() =>
                  onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')
                }
                className="p-2 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                {sortOrder === 'asc' ? (
                  <SortAsc className="w-4 h-4" />
                ) : (
                  <SortDesc className="w-4 h-4" />
                )}
              </button>
            </div>
          )}

          {/* Similarity Mode Indicator */}
          {filters.similarTo && (
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-800 border border-orange-200 rounded-md text-sm font-medium w-fit">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">
                Results sorted by similarity to {filters.similarTo}
              </span>
              <span className="sm:hidden">Similar to {filters.similarTo}</span>
            </div>
          )}
        </div>
      </div>

      {/* Analytics Panel */}
      <AnalyticsPanel
        selectedCases={selectedCases}
        onPrint={handlePrintReport}
        onExport={handleExportReport}
        onClearAll={handleClearAll}
        searchQuery={searchQuery}
        filters={filters}
      />

      {/* Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedResults.map((result, index) => (
          <CaseCard
            key={`${result.caseNumber}-${index}`}
            caseData={result}
            onSelect={onCaseSelect}
            onToggleSelection={handleToggleSelection}
            isSelected={selectedCases.some(
              (selected) => selected.caseNumber === result.caseNumber
            )}
            isSourceCase={
              filters.similarTo &&
              sourceCase &&
              result.caseNumber === sourceCase.caseNumber
            }
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalResults > pagination.pageSize && (
        <div className="bg-white rounded-lg shadow p-4">
          {renderPageNumbers()}
        </div>
      )}
    </div>
  );
};

EnhancedResultsSection.propTypes = {
  results: PropTypes.arrayOf(DocketPropType),
  onCaseSelect: PropTypes.func.isRequired,
  onFindSimilar: PropTypes.func,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number,
    pageSize: PropTypes.number,
    totalResults: PropTypes.number
  }),
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  searchQuery: PropTypes.string,
  filters: PropTypes.object,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  sourceCase: PropTypes.object,
  executionTime: PropTypes.number
};

export default EnhancedResultsSection;
